import {
  CaretSortIcon,
  CheckIcon,
  PlusCircledIcon,
} from '@radix-ui/react-icons';
import * as React from 'react';

import { OrganizationService } from '@/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import CreateNewOrganizationDialog from './create-new-org-dialog';
import { ScrollArea } from './ui/scroll-area';

// const adminUids = [
//   'FZZBdSR14QMSBiFjmocb9DJUeiz1',
//   'xKxDFOcRVWTACjwBWVeGbqnd52g2',
//   'Api5p1zCasOWJfTj9U3bzZKf1dm2',
//   'vNGzgHmHnZQaCuye45KlGQFYga52',
//   'UDkmuqWkYdo5PWCJvD1oGrPnOhSh',
//   'HDrhRLeVDeFiMm8OTT0j3fgzQzSu',
// ];

type PopoverTriggerProps = React.ComponentPropsWithoutRef<
  typeof PopoverTrigger
>;

interface OrgSwitcherProps extends PopoverTriggerProps {}

export default function OrgSwitcher({ className }: OrgSwitcherProps) {
  const [open, setOpen] = React.useState(false); // state to show the popover
  const [showNewOrgDialog, setShowNewOrgDialog] = React.useState(false); // state to show the new organization dialog

  const user = useStore($user);
  const page = useStore($router);

  // query to load the orgs where the current user is a member of.
  const orgs = useQuery({
    queryKey: ['getOrganizations'],
    queryFn: async () => {
      const res = await OrganizationService.getOrganizations();
      return res;
    },
  });

  const handleSelectOrg = (orgId: string) => {
    if (!orgs.data) return;

    const org = orgs.data.find(org => org.id === orgId);
    if (!org) return;

    $orgs.set(orgs.data);
    $org.set(org);

    // also set in local storage
    localStorage.setItem('rallySelectedOrgId', org?.id);

    if (page?.path?.startsWith('/organization')) {
      $router.open('/home');
      $workspace.set(undefined);
    }
  };

  const selectedOrg = useStore($org);

  useEffect(() => {
    // if there is selected org in local storage, then set it as selected org in the store
    const selectedOrgId = localStorage.getItem('rallySelectedOrgId');
    if (selectedOrgId && orgs.isSuccess && orgs.data?.length > 0) {
      const org = orgs.data?.find(org => org.id === selectedOrgId);
      if (org) {
        $org.set(org);
      }
    } else {
      if (orgs.isSuccess && orgs.data?.length > 0 && !selectedOrg) {
        $org.set(orgs.data[0]);
        // also set in local storage since there is no selected org in local storage
        localStorage.setItem('rallySelectedOrgId', orgs.data[0]?.id);
      }
    }
  }, [orgs.isSuccess]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select a team"
            className={cn('w-full justify-between text-ellipsis', className)}
          >
            <Avatar className="mr-2 h-5 w-5">
              <AvatarImage
                src={selectedOrg?.logoUrl || ''}
                alt={selectedOrg?.name}
                className=""
              />
              <AvatarFallback>
                {selectedOrg?.name.slice(0, 2).toLocaleUpperCase()}
              </AvatarFallback>
            </Avatar>
            {selectedOrg?.name || 'Select an organization'}
            <CaretSortIcon className="ml-auto h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0">
          <Command>
            <CommandList>
              <CommandGroup>
                <div className="flex p-2 text-sm">
                  <Avatar className="mr-2 h-10 w-10">
                    <AvatarImage
                      src={selectedOrg?.logoUrl || ''}
                      alt={selectedOrg?.name}
                      className=""
                    />
                    <AvatarFallback>
                      {selectedOrg?.name.slice(0, 2).toLocaleUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm text-muted-foreground ">
                      Selected organization:
                    </p>
                    <div className="text-sm font-semibold">
                      {selectedOrg?.name}
                    </div>
                  </div>
                </div>
              </CommandGroup>
            </CommandList>

            <CommandSeparator />
            <ScrollArea className="h-[290px] p-1">
              <CommandList className="max-h-[1000px]">
                <CommandInput placeholder="Search organization..." />
                <CommandEmpty>No organization found.</CommandEmpty>
                <CommandGroup heading="Organizations">
                  {orgs.data?.map(org => (
                    <CommandItem
                      key={org.id}
                      onSelect={() => {
                        handleSelectOrg(org.id);
                        setOpen(false);
                      }}
                      className="text-sm"
                    >
                      <Avatar className="mr-2 h-5 w-5">
                        <AvatarImage
                          src={org.logoUrl || ''}
                          alt={org.name}
                          className=""
                        />
                        <AvatarFallback>
                          {org.name.slice(0, 2).toLocaleUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {org.name}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          selectedOrg?.id === org.id
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </ScrollArea>

            {/* {user?.uid && adminUids.includes(user.uid) && ( */}
            {user?.uid && (
              <>
                <CommandSeparator />
                <CommandList>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setShowNewOrgDialog(true);
                    }}
                  >
                    <PlusCircledIcon className="mr-2 h-5 w-5" />
                    Create new Organization
                  </CommandItem>
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {/* New organization dialog */}
      <CreateNewOrganizationDialog
        open={showNewOrgDialog}
        setOpen={setShowNewOrgDialog}
      />
    </>
  );
}
